import { Reveal } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { FinalCTA } from "../components/FinalCTA";
import { ServiceCard } from "../components/ServiceCard";
import { CredibilityStrip } from "../components/CredibilityStrip";
import { FloatingMotif } from "../components/FloatingMotif";
import { Icon } from "../components/ServiceIcons";
import { SERVICES } from "../data/site";

export default function Services() {
    return (
        <div data-testid="page-services">
            {/* Header */}
            <section className="relative pt-12 md:pt-20 pb-20 md:pb-28 border-b border-[#dddddd] overflow-hidden">
                <FloatingMotif />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-7">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="h-px w-10 bg-[#111111]" />
                                    <span className="text-[10px] tracking-[0.28em] uppercase font-medium">
                                        Services · Vol. 01
                                    </span>
                                </div>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tight">
                                    Ten quiet ways
                                    <br />
                                    <em>to earn more trust.</em>
                                </h1>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-10 text-lg md:text-xl text-[#444444] max-w-2xl leading-relaxed">
                                    Every service below is built around one
                                    question — what would make a serious buyer
                                    take your business more seriously? Each is
                                    delivered with the rigor of a consulting
                                    engagement, not the rush of a studio
                                    project.
                                </p>
                            </Reveal>
                            <Reveal delay={0.25}>
                                <div className="mt-12 flex flex-wrap gap-4">
                                    <MagneticButton
                                        to="/contact"
                                        testid="services-cta-primary"
                                    >
                                        Discuss your scope
                                    </MagneticButton>
                                    <MagneticButton
                                        to="/process"
                                        testid="services-cta-secondary"
                                        variant="outline"
                                    >
                                        See our process
                                    </MagneticButton>
                                </div>
                            </Reveal>
                        </div>
                        <div className="md:col-span-5">
                            <Reveal delay={0.1}>
                                <ServicesIndex />
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            <CredibilityStrip />

            {/* Services grid — interactive cards */}
            <section className="py-20 md:py-28 bg-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="mb-12 flex items-end justify-between">
                        <div>
                            <p className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                                Index · 01 — 10
                            </p>
                            <h2 className="mt-3 font-serif text-3xl md:text-4xl">
                                Ten services. One system.
                            </h2>
                        </div>
                        <p className="hidden md:block text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                            Hover any card →
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {SERVICES.map((s) => (
                            <ServiceCard key={s.n} service={s} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Engagement options */}
            <section className="bg-[#111111] text-white py-24 md:py-32 relative overflow-hidden">
                <FloatingMotif variant="dark" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-4">
                            <p className="text-[10px] tracking-[0.28em] uppercase text-white/60">
                                § Engagements
                            </p>
                            <Reveal>
                                <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight">
                                    Three quiet
                                    <br />
                                    <em className="text-white/60">ways to work.</em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                { t: "Foundations", d: "A complete presence rebuilt from positioning to launch. Our most common starting point.", e: "site" },
                                { t: "Focused upgrade", d: "A targeted redesign or system addition where foundations exist but performance lags.", e: "redesign" },
                                { t: "Ongoing partnership", d: "Quiet, monthly stewardship — content, reputation, and performance compounding over time.", e: "shield" },
                            ].map((e, i) => (
                                <Reveal key={e.t} delay={i * 0.1}>
                                    <div className="border border-white/15 p-8 h-full hover:-translate-y-1 hover:border-white transition-all duration-700 group">
                                        <Icon name={e.e} className="w-12 h-12 text-white/70 group-hover:text-white transition-colors" />
                                        <p className="mt-8 text-[10px] tracking-[0.28em] uppercase text-white/60">
                                            Engagement {String(i + 1).padStart(2, "0")}
                                        </p>
                                        <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-tight">{e.t}</h3>
                                        <p className="mt-5 text-white/70 leading-relaxed">{e.d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <FinalCTA />
        </div>
    );
}

const ServicesIndex = () => (
    <div className="bg-[#0f0f0f] text-white p-6 md:p-8 relative overflow-hidden">
        <div
            className="absolute inset-0 opacity-[0.15] pointer-events-none"
            style={{
                backgroundImage:
                    "linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "16px 16px",
            }}
        />
        <div className="relative">
            <div className="flex items-center justify-between">
                <p className="text-[10px] tracking-[0.28em] uppercase text-white/60">
                    Service Index
                </p>
                <p className="font-serif italic text-white/60 text-sm">
                    10 systems
                </p>
            </div>
            <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {SERVICES.map((s) => (
                    <li key={s.n} className="flex items-baseline gap-3 py-1.5 border-b border-white/10">
                        <span className="text-[10px] tracking-[0.2em] text-white/40 w-6">
                            {s.n}
                        </span>
                        <span className="font-serif text-base leading-tight">
                            {s.title}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);
